import { memo, useEffect, useState, type MouseEvent } from 'react';
import { Handle, Position, type Node, type NodeProps, type NodeTypes } from '@xyflow/react';
import {
  IconCheck,
  IconChevronRight,
  IconCircleDot,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { Button, cx, IconButton, Switch } from '@wapharma/design-system';
import { apiDelete, apiPatch, apiPut } from '../api';
import { getDisplayError } from '../apiErrors';
import { scheduleUndoableAction } from '../lib/undoableAction';
import { useToast } from '../contexts/ToastContext';
import { MarkeLogoField, MarkeLogoThumb, type MarkeLogoValue } from './MarkeLogoField';
import styles from './ProductTypeTaxonomyGraph.module.css';
import type { ProductTypeRow, CategoryRow } from './ProductTypeTaxonomyGraph';

type EmphasisLevel = 'selected' | 'on-path' | 'muted' | 'neutral' | 'orphan';

type TypeNodeData = {
  kind: 'type';
  type: ProductTypeRow;
  rootOptions: CategoryRow[];
  emphasis: EmphasisLevel;
  selected: boolean;
  canEdit: boolean;
  minimal?: boolean;
  token: string;
  onChange: () => void | Promise<void>;
  onSelectType?: (code: string) => void;
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
  vertical?: boolean;
};

type CategoryNodeData = {
  kind: 'category';
  category: CategoryRow;
  isRoot: boolean;
  hasChildren: boolean;
  childCount: number;
  ownerTypes: ProductTypeRow[];
  emphasis: EmphasisLevel;
  canEdit: boolean;
  minimal?: boolean;
  token: string;
  onChange: () => void | Promise<void>;
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
  vertical?: boolean;
};

/* ──────────────────────────────────────────────────────────────────────────
 * Node cards — registered in `nodeTypes` and rendered by React Flow.
 * Each card is responsible for its own inline edit state + API call.
 * After a successful save we call `data.onChange()` so the parent reloads.
 * ────────────────────────────────────────────────────────────────────────── */

function emphasisClassName(level: EmphasisLevel): string {
  if (level === 'selected') return cx(styles.card, styles.cardSelected);
  if (level === 'on-path') return cx(styles.card, styles.cardOnPath);
  if (level === 'muted') return cx(styles.card, styles.cardMuted);
  if (level === 'orphan') return cx(styles.card, styles.cardNeutral);
  return cx(styles.card, styles.cardNeutral);
}

function ActiveDot({ isActive }: { isActive: boolean }) {
  const label = isActive ? 'Actif' : 'Inactif';
  return (
    <span
      role="img"
      aria-label={label}
      className={cx(styles.activeDot, isActive ? styles.activeDotOn : styles.activeDotOff)}
      title={label}
    />
  );
}

function taxonomyImageValue(row: { imageKey?: string | null; imageUrl?: string | null }): MarkeLogoValue {
  return { key: row.imageKey ?? null, url: row.imageUrl ?? null };
}

function canCycleSeedPhotoUrl(url: string | null | undefined): boolean {
  return Boolean(url && /^https?:\/\//i.test(url));
}

function SeedPhotoCycleThumb({
  url,
  name,
  seedKey,
  onCycleSeedPhoto,
}: {
  url: string | null | undefined;
  name: string;
  seedKey: string;
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
}) {
  const [busy, setBusy] = useState(false);
  const [localUrl, setLocalUrl] = useState(url ?? null);

  useEffect(() => {
    setLocalUrl(url ?? null);
  }, [url]);

  const showCycle = Boolean(onCycleSeedPhoto) && canCycleSeedPhotoUrl(localUrl);

  const cycle = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onCycleSeedPhoto || busy) return;
    setBusy(true);
    try {
      const next = await onCycleSeedPhoto(seedKey);
      if (next) setLocalUrl(next);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={cx(styles.cardMinimalThumb, 'nodrag', 'nopan')}>
      <MarkeLogoThumb url={localUrl} name={name} />
      {showCycle ? (
        <button
          type="button"
          className={cx(styles.seedPhotoCycleBtn, 'nodrag', 'nopan')}
          disabled={busy}
          title="Autre photo Pexels"
          aria-label={`Changer la photo de ${name}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => void cycle(e)}
        >
          <IconRefresh size={10} stroke={2.2} aria-hidden />
        </button>
      ) : null}
    </span>
  );
}

export const TypeNode = memo(function TypeNode({ data }: NodeProps<Node<TypeNodeData>>) {
  const {
    type,
    rootOptions,
    emphasis,
    selected,
    canEdit,
    minimal,
    token,
    onChange,
    onSelectType,
    onCycleSeedPhoto,
    vertical,
  } = data;
  const sourcePos = vertical ? Position.Bottom : Position.Right;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    label: type.label,
    description: type.description ?? '',
    sortOrder: type.sortOrder,
    isActive: type.isActive,
    rootCategoryIds: type.roots.map((r) => r.categoryId),
    image: taxonomyImageValue(type),
  });

  // Reset form when underlying data changes (e.g. parent refreshes after save).
  useEffect(() => {
    if (editing) return;
    setForm({
      label: type.label,
      description: type.description ?? '',
      sortOrder: type.sortOrder,
      isActive: type.isActive,
      rootCategoryIds: type.roots.map((r) => r.categoryId),
      image: taxonomyImageValue(type),
    });
  }, [type, editing]);

  const cancel = () => {
    setForm({
      label: type.label,
      description: type.description ?? '',
      sortOrder: type.sortOrder,
      isActive: type.isActive,
      rootCategoryIds: type.roots.map((r) => r.categoryId),
      image: taxonomyImageValue(type),
    });
    setErr(null);
    setEditing(false);
  };

  const save = async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    setErr(null);
    try {
      await apiPatch(
        `/catalog/admin/product-types/${encodeURIComponent(type.code)}/meta`,
        {
          label: form.label.trim(),
          description: form.description.trim() || null,
          sortOrder: Number(form.sortOrder) || 0,
          isActive: Boolean(form.isActive),
          imageKey: form.image.key,
        },
        token,
      );
      const ids = Array.from(new Set(form.rootCategoryIds.filter(Boolean)));
      await apiPut(
        `/catalog/admin/product-types/${encodeURIComponent(type.code)}/category-roots`,
        { productTypeCode: type.code, categoryIds: ids },
        token,
      );
      setEditing(false);
      await onChange();
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setSaving(false);
    }
  };

  if (minimal) {
    const canSwitch = Boolean(onSelectType) && !selected;
    return (
      <div
        className={cx(emphasisClassName(emphasis), styles.cardMinimal)}
        data-card-kind="type"
        data-card-emphasis={emphasis}
      >
        <Handle type="source" position={sourcePos} id="out" />
        <SeedPhotoCycleThumb
          url={type.imageUrl}
          name={type.label}
          seedKey={type.code}
          onCycleSeedPhoto={onCycleSeedPhoto}
        />
        <div
          className={cx(styles.cardMinimalText, canSwitch && 'nodrag', canSwitch && 'nopan')}
          role={canSwitch ? 'button' : undefined}
          tabIndex={canSwitch ? 0 : undefined}
          onPointerDown={canSwitch ? (e) => e.stopPropagation() : undefined}
          onClick={canSwitch ? () => onSelectType?.(type.code) : undefined}
          onKeyDown={
            canSwitch
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectType?.(type.code);
                  }
                }
              : undefined
          }
        >
          <span className={styles.cardMinimalKind}>Type</span>
          <span className={styles.cardMinimalTitle} title={type.label}>
            {type.label || type.code}
          </span>
          <span className={styles.cardMinimalSub} title={type.code}>
            {type.code}
          </span>
        </div>
        {selected ? <span className={styles.cardMinimalMark} aria-label="Sélectionné" /> : null}
      </div>
    );
  }

  return (
    <div className={emphasisClassName(emphasis)} data-card-kind="type" data-card-emphasis={emphasis}>
      {/* No incoming handle — types are roots of the tree (column 0). */}
      <Handle type="source" position={sourcePos} id="out" />

      <div className={styles.cardHead}>
        <MarkeLogoThumb url={editing ? form.image.url : type.imageUrl} name={type.label} />
        <div className={styles.cardTitleBlock}>
          <div className={styles.cardTitle} title={type.label}>
            {type.label || type.code}
          </div>
          <div className={styles.cardSubtitle}>{type.code}</div>
        </div>
        <div className={cx(styles.cardActions, 'nodrag', 'nopan')}>
          <ActiveDot isActive={form.isActive} />
          {!editing && canEdit ? (
            <IconButton
              type="button"
              size="xs"
              variant="ghost"
              className={cx(styles.iconBtn, 'nodrag', 'nopan')}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setEditing(true)}
              aria-label={`Modifier ${type.label}`}
            >
              <IconPencil size={14} stroke={1.8} aria-hidden />
            </IconButton>
          ) : null}
        </div>
      </div>

      {!editing ? (
        <TypeNodeReadView
          type={type}
          emphasis={emphasis}
          selected={selected}
          canSwitch={Boolean(onSelectType) && !selected && emphasis !== 'selected'}
          onSwitch={() => onSelectType?.(type.code)}
        />
      ) : (
        <TypeNodeEditView
          form={form}
          setForm={setForm}
          rootOptions={rootOptions}
          saving={saving}
          err={err}
          canEdit={canEdit}
          token={token}
          onSave={save}
          onCancel={cancel}
        />
      )}
    </div>
  );
});

function TypeNodeReadView({
  type,
  emphasis,
  selected,
  canSwitch,
  onSwitch,
}: {
  type: ProductTypeRow;
  emphasis: EmphasisLevel;
  selected: boolean;
  canSwitch: boolean;
  onSwitch: () => void;
}) {
  return (
    <div className={styles.cardBody}>
      {type.description ? <div className={styles.descriptionBlock}>{type.description}</div> : null}
      <div className={styles.metaRow}>
        <span className={styles.metaRowLabel}>Ordre</span>
        <span className={styles.metaRowValue}>{type.sortOrder}</span>
      </div>
      <div className={styles.metaRow}>
        <span className={styles.metaRowLabel}>Racines</span>
        <span className={styles.metaRowValue}>{type.roots.length}</span>
      </div>
      <div className={styles.badges}>
        {selected ? (
          <span className={cx(styles.badge, styles.badgeAccent)}>Sélectionné</span>
        ) : emphasis === 'muted' ? (
          <span className={cx(styles.badge, styles.badgeMuted)}>Hors contexte</span>
        ) : null}
      </div>
      {canSwitch ? (
        <div className={styles.footerActions}>
          <Button type="button" variant="ghost" size="xs" onClick={onSwitch}>
            Voir ce type
            <IconChevronRight size={12} stroke={1.8} aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function TypeNodeEditView({
  form,
  setForm,
  rootOptions,
  saving,
  err,
  canEdit,
  token,
  onSave,
  onCancel,
}: {
  form: {
    label: string;
    description: string;
    sortOrder: number;
    isActive: boolean;
    rootCategoryIds: string[];
    image: MarkeLogoValue;
  };
  setForm: (
    updater:
      | {
          label: string;
          description: string;
          sortOrder: number;
          isActive: boolean;
          rootCategoryIds: string[];
          image: MarkeLogoValue;
        }
      | ((prev: typeof form) => typeof form),
  ) => void;
  rootOptions: CategoryRow[];
  saving: boolean;
  err: string | null;
  canEdit: boolean;
  token: string;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
}) {
  return (
    <div className={cx(styles.editForm, 'nodrag', 'nopan')}>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <span className={styles.editLabel}>Image</span>
        <MarkeLogoField
          token={token}
          value={form.image}
          onChange={(image) => setForm((p) => ({ ...p, image }))}
          disabled={!canEdit || saving}
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Libellé</label>
        <input
          className={styles.editInput}
          value={form.label}
          onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
          disabled={!canEdit}
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Description</label>
        <textarea
          className={styles.editTextarea}
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          disabled={!canEdit}
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Ordre</label>
        <input
          className={cx(styles.editInput, styles.editInputMono)}
          inputMode="numeric"
          value={String(form.sortOrder)}
          onChange={(e) => setForm((p) => ({ ...p, sortOrder: Number(e.target.value) || 0 }))}
          disabled={!canEdit}
        />
      </div>
      <div className={cx(styles.editSwitchRow, 'nodrag', 'nopan')}>
        <span className={styles.editSwitchLabel}>Actif</span>
        <Switch
          checked={form.isActive}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            setForm((p) => ({ ...p, isActive: checked }));
          }}
          disabled={!canEdit}
          aria-label="Actif"
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Catégories racines</label>
        <div className={styles.rootSelector}>
          {rootOptions.length === 0 ? (
            <span className={styles.rootSelectorEmpty}>Aucune catégorie racine.</span>
          ) : (
            rootOptions.map((c) => {
              const checked = form.rootCategoryIds.includes(c.id);
              return (
                <label key={c.id} className={styles.rootSelectorRow}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setForm((p) => {
                        const next = new Set(p.rootCategoryIds);
                        if (on) next.add(c.id);
                        else next.delete(c.id);
                        return { ...p, rootCategoryIds: Array.from(next) };
                      });
                    }}
                  />
                  <span>
                    <strong className={styles.rootSelectorName}>{c.name}</strong>
                    <span className={styles.rootSelectorSlug}>{c.slug}</span>
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
      {err ? <div className={styles.editError}>{err}</div> : null}
      <div className={styles.editButtons}>
        <Button type="button" variant="ghost" size="xs" onClick={onCancel} disabled={saving}>
          <IconX size={12} stroke={1.8} aria-hidden /> Annuler
        </Button>
        <Button
          type="button"
          variant="primary"
          size="xs"
          disabled={!canEdit || saving || !form.label.trim()}
          onClick={() => void onSave()}
        >
          <IconCheck size={12} stroke={1.8} aria-hidden /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}

export const CategoryNode = memo(function CategoryNode({ data }: NodeProps<Node<CategoryNodeData>>) {
  const {
    category,
    isRoot,
    hasChildren,
    childCount,
    ownerTypes,
    emphasis,
    canEdit,
    minimal,
    token,
    onChange,
    onCycleSeedPhoto,
    vertical,
  } = data;
  const sourcePos = vertical ? Position.Bottom : Position.Right;
  const targetPos = vertical ? Position.Top : Position.Left;
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: category.name,
    slug: category.slug,
    image: taxonomyImageValue(category),
  });

  useEffect(() => {
    if (editing) return;
    setForm({ name: category.name, slug: category.slug, image: taxonomyImageValue(category) });
  }, [category, editing]);

  const cancel = () => {
    setForm({ name: category.name, slug: category.slug, image: taxonomyImageValue(category) });
    setErr(null);
    setEditing(false);
  };

  const save = async () => {
    if (!canEdit || saving) return;
    const name = form.name.trim();
    const slug = form.slug.trim();
    if (!name || !slug) {
      setErr('Nom et slug obligatoires.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await apiPatch(
        `/catalog/admin/categories/${encodeURIComponent(category.id)}`,
        { name, slug, imageKey: form.image.key },
        token,
      );
      setEditing(false);
      await onChange();
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = () => {
    if (!canEdit) return;
    scheduleUndoableAction({
      key: `category-node:${category.id}`,
      message: `Catégorie « ${category.name} » supprimée`,
      notify: showToast,
      apply: () => setPendingRemove(true),
      revert: () => setPendingRemove(false),
      commit: async () => {
        await apiDelete(`/catalog/admin/categories/${encodeURIComponent(category.id)}`, token);
        await onChange();
      },
      commitErrorMessage: `Impossible de supprimer « ${category.name} ».`,
    });
  };

  if (pendingRemove) return null;

  if (minimal) {
    return (
      <div
        className={cx(emphasisClassName(emphasis), styles.cardMinimal)}
        data-card-kind="category"
        data-card-emphasis={emphasis}
      >
        <Handle type="target" position={targetPos} id="in" />
        {hasChildren ? <Handle type="source" position={sourcePos} id="out" /> : null}
        <SeedPhotoCycleThumb
          url={category.imageUrl}
          name={category.name}
          seedKey={category.slug}
          onCycleSeedPhoto={onCycleSeedPhoto}
        />
        <div className={styles.cardMinimalText}>
          <span className={styles.cardMinimalKind}>{isRoot ? 'Cat.' : 'Sous-cat.'}</span>
          <span className={styles.cardMinimalTitle} title={category.name}>
            {category.name}
          </span>
          <span className={styles.cardMinimalSub} title={category.slug}>
            {childCount > 0 ? `${childCount} enfant${childCount > 1 ? 's' : ''}` : category.slug}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={emphasisClassName(emphasis)} data-card-kind="category" data-card-emphasis={emphasis}>
      <Handle type="target" position={targetPos} id="in" />
      {hasChildren ? <Handle type="source" position={sourcePos} id="out" /> : null}

      <div className={styles.cardHead}>
        <MarkeLogoThumb url={editing ? form.image.url : category.imageUrl} name={category.name} />
        <div className={styles.cardTitleBlock}>
          <div className={styles.cardTitle} title={category.name}>
            {category.name}
          </div>
          <div className={styles.cardSubtitle}>{category.slug}</div>
        </div>
        <div className={cx(styles.cardActions, 'nodrag', 'nopan')}>
          {!editing && canEdit ? (
            <>
              <IconButton
                type="button"
                size="xs"
                variant="ghost"
                className={cx(styles.iconBtn, 'nodrag', 'nopan')}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setEditing(true)}
                aria-label={`Modifier ${category.name}`}
              >
                <IconPencil size={14} stroke={1.8} aria-hidden />
              </IconButton>
              <IconButton
                type="button"
                size="xs"
                variant="ghost"
                className={cx(styles.iconBtn, styles.iconBtnDanger, 'nodrag', 'nopan')}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={confirmRemove}
                aria-label={`Supprimer ${category.name}`}
              >
                <IconTrash size={14} stroke={1.8} aria-hidden />
              </IconButton>
            </>
          ) : null}
        </div>
      </div>

      {!editing ? (
        <CategoryNodeReadView
          isRoot={isRoot}
          childCount={childCount}
          ownerTypes={ownerTypes}
          emphasis={emphasis}
        />
      ) : (
        <CategoryNodeEditView
          form={form}
          setForm={setForm}
          saving={saving}
          err={err}
          canEdit={canEdit}
          token={token}
          onSave={save}
          onCancel={cancel}
        />
      )}
    </div>
  );
});

function CategoryNodeReadView({
  isRoot,
  childCount,
  ownerTypes,
  emphasis,
}: {
  isRoot: boolean;
  childCount: number;
  ownerTypes: ProductTypeRow[];
  emphasis: EmphasisLevel;
}) {
  return (
    <div className={styles.cardBody}>
      <div className={styles.metaRow}>
        <span className={styles.metaRowLabel}>{isRoot ? 'Racine' : 'Sous-cat.'}</span>
        <span className={styles.metaRowValue}>
          {childCount} {childCount === 1 ? 'enfant' : 'enfants'}
        </span>
      </div>
      <div className={styles.badges}>
        {emphasis === 'orphan' ? <span className={cx(styles.badge, styles.badgeOrphan)}>Non assigné</span> : null}
        {ownerTypes.length === 0 && emphasis !== 'orphan' ? (
          <span className={cx(styles.badge, styles.badgeMuted)}>Sans type</span>
        ) : null}
        {ownerTypes.map((t) => (
          <span
            key={t.code}
            className={cx(styles.badge, emphasis === 'on-path' ? styles.badgeAccent : styles.badgeMuted)}
            title={t.label}
          >
            <IconCircleDot size={10} stroke={2} aria-hidden />
            {t.code}
          </span>
        ))}
      </div>
    </div>
  );
}

function CategoryNodeEditView({
  form,
  setForm,
  saving,
  err,
  canEdit,
  token,
  onSave,
  onCancel,
}: {
  form: { name: string; slug: string; image: MarkeLogoValue };
  setForm: (
    updater:
      | { name: string; slug: string; image: MarkeLogoValue }
      | ((p: { name: string; slug: string; image: MarkeLogoValue }) => { name: string; slug: string; image: MarkeLogoValue }),
  ) => void;
  saving: boolean;
  err: string | null;
  canEdit: boolean;
  token: string;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
}) {
  return (
    <div className={cx(styles.editForm, 'nodrag', 'nopan')}>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <span className={styles.editLabel}>Image</span>
        <MarkeLogoField
          token={token}
          value={form.image}
          onChange={(image) => setForm((p) => ({ ...p, image }))}
          disabled={!canEdit || saving}
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Nom</label>
        <input
          className={styles.editInput}
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          disabled={!canEdit}
        />
      </div>
      <div className={cx(styles.editFieldRow, 'nodrag', 'nopan')}>
        <label className={styles.editLabel}>Slug</label>
        <input
          className={cx(styles.editInput, styles.editInputMono)}
          value={form.slug}
          onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
          disabled={!canEdit}
        />
      </div>
      {err ? <div className={styles.editError}>{err}</div> : null}
      <div className={cx(styles.editButtons, 'nodrag', 'nopan')}>
        <Button type="button" variant="ghost" size="xs" onClick={onCancel} disabled={saving}>
          <IconX size={12} stroke={1.8} aria-hidden /> Annuler
        </Button>
        <Button
          type="button"
          variant="primary"
          size="xs"
          disabled={!canEdit || saving || !form.name.trim() || !form.slug.trim()}
          onClick={() => void onSave()}
        >
          <IconCheck size={12} stroke={1.8} aria-hidden /> {saving ? '…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}

export const TAXONOMY_GRAPH_NODE_TYPES: NodeTypes = {
  type: TypeNode as unknown as NodeTypes[string],
  category: CategoryNode as unknown as NodeTypes[string],
};
